import { z } from 'zod'
import type { OrgStatus, OrgUnitKind } from './org-units.js'

/**
 * Org-unit CSV import (recruitment/01 §3): a ministry has hundreds of units and nobody types them.
 * The file is diffed by code against the tree; the preview shows every create, rename, move, archive
 * and restore; the apply re-plans in one transaction and refuses with 409 `IMPORT_STALE` when the plan
 * no longer has the fingerprint the user saw. Units missing from the file are left alone: only a row
 * with `status=ARCHIVED` archives.
 */

/** The columns, in template order. `code` and `name` are required; the rest may be blank. */
export const ORG_IMPORT_COLUMNS = ['code', 'name', 'name_ar', 'kind', 'parent_code', 'status'] as const
export type OrgImportColumn = (typeof ORG_IMPORT_COLUMNS)[number]

/** Large enough for thousands of units; the JSON body limit is 1 MB. */
export const ORG_IMPORT_MAX_CSV = 900_000
export const ORG_IMPORT_MAX_ROWS = 5_000

export const orgImportPreviewSchema = z.object({
  csv: z.string().min(1, 'The file is empty').max(ORG_IMPORT_MAX_CSV, 'The file is too large'),
})
export type OrgImportPreviewInput = z.input<typeof orgImportPreviewSchema>

export const orgImportApplySchema = orgImportPreviewSchema.extend({
  fingerprint: z.string().regex(/^[0-9a-f]{64}$/, 'Preview the file first'),
})
export type OrgImportApplyInput = z.input<typeof orgImportApplySchema>

export type OrgImportAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'ERROR'
/** What an UPDATE (or a CREATE of an archived unit) changes. */
export type OrgImportChange = 'RENAME' | 'KIND' | 'MOVE' | 'ARCHIVE' | 'RESTORE'

/**
 * Why a row or the file cannot be imported. The UI translates by code with `params`; `message` is the
 * English fallback. A row may also carry an archive blocker's own code (ORG_UNIT_HAS_MEMBERS, … with
 * `n`), as the archive command refuses.
 */
export const ORG_IMPORT_ERROR_CODES = [
  // the file
  'CSV_PARSE',
  'FILE_EMPTY',
  'UNKNOWN_COLUMN',
  'DUPLICATE_COLUMN',
  'MISSING_COLUMN',
  'TOO_MANY_ROWS',
  // a row
  'CODE_INVALID',
  'CODE_DUPLICATE',
  'TOO_MANY_FIELDS',
  'NAME_REQUIRED',
  'TOO_LONG',
  'BAD_KIND',
  'BAD_STATUS',
  'PARENT_UNKNOWN',
  'CYCLE',
  'PARENT_ARCHIVED',
  'ARCHIVED_LOCKED',
  'HAS_CHILDREN',
] as const
export type OrgImportErrorCode = (typeof ORG_IMPORT_ERROR_CODES)[number]

/** Values for the translated text: `line`, `column`, `max`, `kinds`, `n`. */
export type OrgImportErrorParams = Record<string, string | number>

export interface OrgImportFileError {
  code: OrgImportErrorCode
  params?: OrgImportErrorParams
  message: string
}

export interface OrgImportRowError {
  /** A column name, or `row` for the row as a whole. */
  field: OrgImportColumn | 'row'
  /** An OrgImportErrorCode, or an archive blocker's code (OrgUnitErrorCode). */
  code: string
  params?: OrgImportErrorParams
  message: string
}

/** A unit as the file says it should be (or as it is now, for `before`). */
export interface OrgImportUnitState {
  name: string
  nameAr: string | null
  kind: OrgUnitKind
  parentCode: string | null
  status: OrgStatus
}

export interface OrgImportRow {
  /** 1-based line in the file. */
  line: number
  code: string
  action: OrgImportAction
  changes: OrgImportChange[]
  /** The unit after the import (null when the row could not be read). */
  after: OrgImportUnitState | null
  /** The unit now (null for a new unit). */
  before: OrgImportUnitState | null
  errors: OrgImportRowError[]
}

export interface OrgImportCounts {
  rows: number
  create: number
  update: number
  unchanged: number
  error: number
  rename: number
  move: number
  archive: number
  restore: number
}

export interface OrgImportPlan {
  /** Pass back to the apply; it changes when the file or the tree changes. */
  fingerprint: string
  counts: OrgImportCounts
  /** Errors about the file as a whole (missing columns, unreadable CSV). */
  fileErrors: OrgImportFileError[]
  rows: OrgImportRow[]
}

export interface OrgImportResult {
  counts: OrgImportCounts
}
